import React, { use, useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  Divider,
  Input,
  Select,
  Table,
  ConfigProvider,
  Tabs,
  Tag,
  Progress,
} from "antd";
import { SearchOutlined, UnorderedListOutlined } from "@ant-design/icons";
import Text from "../../components/Text/Text";
import { icon } from "../../resource";
import { FlexCol } from "../../components/Container/Container";
import Button from "../../components/Button/Button";
import { CardContainer } from "../../components/Card/CardContainer";
import PageTitleNested from "../../components/PageTitle/PageTitleNested";
import BreadCrumb from "../../components/BreadCrumb/BreadCrumb";
import { useNavigate } from "react-router";
import { ActionBtn } from "../../components/Button/TableActionButton";
import dayjs from "dayjs";
import {
  getFlashSaleTrackingRound,
  getFlashSaleTrackingRoundOrder,
  getFlashSaleTrackingRoundShop,
} from "../../datasource/FlashSaleDatasource";
import { numberFormatter } from "../../utility/Formatter";
import {
  mapBgColorRound,
  mapQuotaType,
  mapStatusRound,
  mapTextColorRound,
} from "../../definitions/flashSaleStatus";
import { ORDER_STATUS } from "../../definitions/orderStatus";
import { getOrderStatus } from "../../utility/OrderStatus";

export const TrackingFlashSaleDetail = () => {
  const profile = JSON.parse(localStorage.getItem("profile")!);
  const digitDecimal = profile?.company === "ICPL" ? 0 : 2;
  const { pathname } = window.location;
  const pathSplit = pathname.split("/") as Array<string>;
  const flashSaleId = pathSplit[4];
  const navigate = useNavigate();
  const [trackingRoundData, setTrackingRoundData] = useState<any>();
  const [dataRounds, setDataRounds] = useState<Array<any>>([]);
  const [dataRoundShop, setDataRoundShop] = useState<any>();
  const [dataRoundOrder, setDataRoundOrder] = useState<any>();
  const [currentRound, setCurrentRound] = useState<any>(undefined);
  const [roundId, setRoundId] = useState<any>(pathSplit[5]);
  const [liveRound, setLiveRound] = useState<any>(undefined);

  const [searchOrder, setSearchOrder] = useState<{
    flashSaleRoundId: string;
    search: string | undefined;
    zone: string | undefined;
    customerGroupName: string | undefined;
    page: number;
    take: number;
  }>({
    flashSaleRoundId: roundId,
    search: undefined,
    zone: undefined,
    customerGroupName: undefined,
    page: 1,
    take: 10,
  });

  const fetchDataRound = async () => {
    const { responseData } = await getFlashSaleTrackingRound({
      flashSaleId: flashSaleId,
      ...(roundId !== "ALL" && { flashSaleRoundId: roundId }),
    });
    const findLive =
      responseData?.rounds?.find((round: any) => round?.status === "LIVE")?.roundNo ||
      responseData?.rounds?.filter((round: any) => round?.status === "ENDED")?.length + 1 ||
      1;
    setLiveRound(findLive);
    setTrackingRoundData(responseData);
    setDataRounds(responseData?.rounds || []);
    setCurrentRound(responseData?.round?.roundNo || "ALL");
  };
  const fetchDataRoundShop = async () => {
    const { responseData } = await getFlashSaleTrackingRoundShop({
      flashSaleId: flashSaleId,
      flashSaleRoundId: roundId === "ALL" ? null : roundId,
    });
    setDataRoundShop(responseData || []);
  };
  const fetchDataRoundOrder = async () => {
    const { responseData } = await getFlashSaleTrackingRoundOrder({
      flashSaleId: flashSaleId,
      flashSaleRoundId:
        searchOrder.flashSaleRoundId === "ALL" ? undefined : searchOrder.flashSaleRoundId,
      search: searchOrder.search,
      zone: searchOrder.zone,
      customerGroupName: searchOrder.customerGroupName,
      page: searchOrder.page,
      take: searchOrder.take,
    });
    setDataRoundOrder(responseData || []);
  };

  useEffect(() => {
    fetchDataRound();
    fetchDataRoundShop();
  }, [roundId]);

  useEffect(() => {
    fetchDataRoundOrder();
  }, [searchOrder]);

  const PageTitle = () => {
    return (
      <PageTitleNested
        title=' รายละเอียด Flash Sale'
        customBreadCrumb={
          <BreadCrumb
            data={[
              {
                text: "รายการ Flash Sale",
                path: "/PromotionPage/flashSale",
              },
              {
                text: "รายละเอียด Flash Sale",
                path: `/PromotionPage/detailFlashSale/${flashSaleId}/tracking`,
              },
              {
                text: "ภาพรวมทั้งแคมเปญ",
                path: window.location.pathname,
              },
            ]}
          />
        }
        onBack={() => navigate(`/PromotionPage/detailFlashSale/${flashSaleId}/tracking`)}
      />
    );
  };

  const roundInfo = [
    {
      label: "ยอดขายรวม",
      description: "ยอดจริงได้รับการยืนยัน",
      value: `${numberFormatter(trackingRoundData?.summary?.totalSales || 0, 2)} ฿`,
      percentage: null,
      bgColor: null,
      hidden: false,
    },
    {
      label: "จำนวนที่ขายได้ (หน่วย)",
      description: "ออเดอร์ที่ร้านยืนยันคำสั่งซื้อ",
      value: numberFormatter(trackingRoundData?.summary?.soldUnits || 0, digitDecimal),
      percentage: null,
      bgColor: null,
      hidden: false,
    },
    {
      label: "ออเดอร์ทั้งหมด (รายการ)",
      description: "รวมทุกสถานะ",
      value: trackingRoundData?.summary?.orderCount || 0,
      percentage: null,
      bgColor: null,
      hidden: false,
    },
    {
      label: "ร้านที่ซื้อ",
      description: `จาก ${trackingRoundData?.summary?.totalShopCount || 0} ร้าน`,
      value: trackingRoundData?.summary?.buyingShopCount || 0,
      percentage: `${numberFormatter(trackingRoundData?.summary?.buyingShopPercent || 0, 2)}%`,
      bgColor: null,
      hidden: false,
    },
  ];

  return (
    <CardContainer>
      <PageTitle />
      <Divider />
      <div style={{ marginBottom: "16px" }}>
        <Text color='Text3' level={6}>
          อัปเดตล่าสุด{" "}
          {dayjs(trackingRoundData?.fetchedAt)
            .locale("th")
            .add(543, "year")
            .format("DD MMM YYYY • HH:mm:ss")}
        </Text>
      </div>
      <Row justify={"start"} gutter={16}>
        <Col>
          <Button
            title='ทั้งแคมเปญ'
            height={32}
            typeButton={currentRound === "ALL" ? "navy-blue" : "light-navy-blue"}
            onClick={() => {
              setRoundId(undefined);
              setCurrentRound("ALL");
              setSearchOrder((prev) => ({ ...prev, flashSaleRoundId: "ALL" }));
            }}
          />
        </Col>
        {dataRounds?.map((item) => (
          <Col key={item?.roundNo}>
            <Button
              title={`รอบที่ ${item?.roundNo}`}
              height={32}
              typeButton={
                `${currentRound}` === `${item?.roundNo}` ? "navy-blue" : "light-navy-blue"
              }
              onClick={() => {
                setCurrentRound(item?.roundNo.toString());
                setRoundId(item?.flashSaleRoundId);
                setSearchOrder((prev) => ({ ...prev, flashSaleRoundId: item?.flashSaleRoundId }));
              }}
            />
          </Col>
        ))}
      </Row>
      <br />
      <Row justify={"start"} gutter={8}>
        <Col>
          <Text color='Text3' level={6}>
            รอบการจัดแคมเปญ &nbsp;&nbsp;
            <Text level={6}>
              {currentRound === "ALL"
                ? `${dayjs(trackingRoundData?.startDate)
                    .locale("th")
                    .add(543, "year")
                    .format("DD MMM YYYY (HH:mm)")}
             -
              ${dayjs(trackingRoundData?.endDate)
                .locale("th")
                .add(543, "year")
                .format("DD MMM YYYY (HH:mm)")}`
                : `${dayjs(trackingRoundData?.round?.startDate)
                    .locale("th")
                    .add(543, "year")
                    .format("DD MMM YYYY (HH:mm)")}
             -
              ${dayjs(trackingRoundData?.round?.endDate)
                .locale("th")
                .add(543, "year")
                .format("DD MMM YYYY (HH:mm)")}`}
            </Text>
          </Text>
        </Col>
        <Col>
          {(() => {
            const mapStatus =
              trackingRoundData?.round?.roundNo > liveRound
                ? "WAIT"
                : trackingRoundData?.round?.status;
            return (
              <Tag
                style={{
                  backgroundColor: mapBgColorRound[mapStatus],
                  border: "none",
                }}
              >
                <Text color={mapTextColorRound[mapStatus]} level={6} fontWeight={600}>
                  {mapStatusRound[mapStatus]}
                </Text>
              </Tag>
            );
          })()}
        </Col>
      </Row>
      <br />
      <Row justify='space-between' gutter={16}>
        {roundInfo?.map((item: any, index) => (
          <>
            <Col span={24 / roundInfo?.filter((item: any) => !item.hidden).length} key={index}>
              <Card bodyStyle={{ padding: 12 }} style={{ backgroundColor: item.bgColor }}>
                <FlexCol>
                  <Text color='Text3'>{item.label}</Text>
                  <Text fontSize={38} fontWeight={700}>
                    {item.value}{" "}
                    {item.percentage && (
                      <Text color='primary' fontWeight={700}>
                        {item.percentage}
                      </Text>
                    )}
                  </Text>
                  <Text color={index === 3 ? "BK" : "Text3"} fontWeight={index === 3 ? 700 : 400}>
                    {item.description}
                  </Text>
                </FlexCol>
              </Card>
            </Col>
          </>
        ))}
      </Row>
      <br />
      <SectionShop
        flashSaleId={flashSaleId}
        data={dataRoundShop?.data}
        filter={trackingRoundData?.filterOptions}
        currentRound={currentRound}
        roundId={roundId}
      />
      <br />
      <Row justify='space-between' gutter={16}>
        <Col span={currentRound !== "ALL" ? 12 : 24}>
          <SectionProductAmount data={trackingRoundData?.productSales} />
        </Col>
        {currentRound !== "ALL" && (
          <Col span={12}>
            <SectionProductUnit
              data={trackingRoundData?.quotaProducts}
              dataCount={trackingRoundData?.quotaTypeCounts}
              company={profile?.company}
            />
          </Col>
        )}
      </Row>
      <br />
      <SectionOrder
        data={dataRoundOrder}
        company={profile?.company}
        filter={trackingRoundData?.filterOptions}
        currentRound={currentRound}
        dataRounds={dataRounds}
        searchOrder={searchOrder}
        setSearchOrder={setSearchOrder}
      />
    </CardContainer>
  );
};

const SectionShop = ({
  flashSaleId,
  data,
  filter,
  currentRound,
  roundId,
}: {
  flashSaleId: string;
  data: any;
  filter: any;
  currentRound: any;
  roundId: any;
}) => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState<any>(undefined);
  const [searchZone, setSearchZone] = useState<any>(undefined);
  const [searchCustomerGroup, setSearchCustomerGroup] = useState<any>(undefined);

  const searchShop = useMemo(() => {
    return data?.filter((item: any) => {
      const matchOrder =
        !searchText ||
        `${item.customerName}`.toLowerCase().includes(searchText.toLowerCase()) ||
        `${item.customerNo}`.toLowerCase().includes(searchText.toLowerCase());
      const matchZone = !searchZone || `${item.zone}` === `${searchZone}`;
      const matchCustomerGroup =
        !searchCustomerGroup || `${item.customerGroupName}` === `${searchCustomerGroup}`;
      return matchOrder && matchZone && matchCustomerGroup;
    });
  }, [data, searchText, searchZone, searchCustomerGroup]);

  const onSearchShop = (searchValue: string, zone: any, customerGroup: any) => {
    setSearchText(searchValue);
    setSearchZone(zone);
    setSearchCustomerGroup(customerGroup);
  };

  const column = [
    {
      title: "#",
      dataIndex: "no",
      key: "no",
      width: "4%",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{index + 1}</Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "ชื่อร้าน",
      dataIndex: "customerName",
      key: "customerName",
      width: "50%",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{value}</Text>
              <Text color='Text3'>
                {row.customerNo} • เขต {row.zone} • กลุ่ม {row.customerGroupName || "-"}
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "ยอดสั่งซื้อ (฿)",
      dataIndex: "totalSales",
      key: "totalSales",
      width: "30%",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{numberFormatter(value, 2)}</Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "",
      dataIndex: "action",
      key: "action",
      width: "8%",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <ActionBtn
                onClick={() => {
                  navigate(
                    "/PromotionPage/detailFlashSale/tracking/shop/" +
                      flashSaleId +
                      "/" +
                      row.customerCompanyId +
                      "/" +
                      roundId +
                      "/" +
                      currentRound,
                  );
                  window.scrollTo({
                    top: 0,
                    behavior: "smooth",
                  });
                }}
                icon={<UnorderedListOutlined />}
              />
            </FlexCol>
          ),
        };
      },
    },
  ];

  return (
    <Card>
      <div style={{ marginBottom: "10px" }}>
        <Text fontWeight={700} level={4}>
          ยอดสั่งซื้อร้านค้า
        </Text>
      </div>
      <Row justify={"start"} gutter={8} style={{ paddingBottom: "16px" }}>
        <Col span={8}>
          <Input
            value={searchText}
            placeholder='ค้นหารหัส/ชื่อร้านค้า'
            style={{ width: "100%", height: "40px" }}
            suffix={<SearchOutlined />}
            allowClear
            onChange={(e) => {
              setSearchText(e.target.value);
              onSearchShop(e.target.value, searchZone, searchCustomerGroup);
            }}
          />
        </Col>
        <Col span={3}>
          <Select
            value={searchZone}
            allowClear
            options={
              filter?.zones?.map((zone) => ({
                label: zone,
                value: zone,
                key: zone,
              })) || []
            }
            placeholder='เขตทั้งหมด'
            style={{ width: "100%", height: "40px" }}
            onChange={(e) => {
              setSearchZone(e);
              onSearchShop(searchText, e, searchCustomerGroup);
            }}
          />
        </Col>
        <Col span={4}>
          <Select
            value={searchCustomerGroup}
            allowClear
            options={
              filter?.customerGroupNames?.map((group: any) => ({
                label: group,
                value: group,
                key: group,
              })) || []
            }
            placeholder='กลุ่มลูกค้าทั้งหมด'
            style={{ width: "100%", height: "40px" }}
            onChange={(e) => {
              setSearchCustomerGroup(e);
              onSearchShop(searchText, searchZone, e);
            }}
          />
        </Col>
        <Col span={3}>
          <Button
            typeButton='primary-light'
            title='ล้างการค้นหา'
            onClick={() => {
              setSearchZone(undefined);
              setSearchCustomerGroup(undefined);
              setSearchText("");
              onSearchShop("", undefined, undefined);
            }}
          />
        </Col>
        <Col flex='1' style={{ paddingTop: "8px", textAlign: "right" }}>
          <Text color='Text3'>{searchShop?.length || 0} ร้าน</Text>
        </Col>
      </Row>
      <ConfigProvider
        theme={{
          components: {
            Table: {
              headerBg: "#F3F6F9",
              headerColor: "black",
              fontFamily: "IBM Plex Sans Thai",
              fontSize: 15,
              //rowHoverBg: "none",
            },
          },
        }}
      >
        <Table
          columns={column}
          dataSource={searchShop || []}
          pagination={false}
          scroll={{ y: 400 }}
        />
      </ConfigProvider>
    </Card>
  );
};

const SectionProductAmount = ({ data }: { data: any }) => {
  return (
    <Card bodyStyle={{ height: "548px" }}>
      <div style={{ marginBottom: "10px" }}>
        <Text fontWeight={700} level={4}>
          ยอดขายสินค้า
        </Text>
      </div>
      <div style={{ maxHeight: 400, overflowY: "auto" }}>
        {data?.map((item, index) => (
          <React.Fragment key={index}>
            <Row justify='space-between' style={{ padding: "5px" }}>
              <Col>
                <Text>
                  {index + 1}. {item.productName} &nbsp;
                  <Text color='Text3'>({item.packSize})</Text>
                </Text>
              </Col>
              <Col>
                <Text fontWeight={600}>{numberFormatter(item.salesAmount ?? 0, 2)} ฿</Text>
              </Col>
            </Row>
            <Divider size='small' />
          </React.Fragment>
        ))}
      </div>
    </Card>
  );
};

const SectionProductUnit = ({
  data,
  dataCount,
  company,
}: {
  data: any;
  dataCount: any;
  company: string;
}) => {
  const [tabMode, setTabMode] = useState("ALL");
  const digitDecimal = company === "ICPL" ? 0 : 2;

  return (
    <Card bodyStyle={{ height: "548px" }}>
      <div style={{ marginBottom: "10px" }}>
        <Text fontWeight={700} level={4}>
          จำนวนสินค้าที่ขายได้ (หน่วย)
        </Text>
      </div>
      <Tabs
        style={{ fontFamily: "IBM Plex Sans Thai", fontSize: "16px" }}
        activeKey={tabMode}
        onChange={(key) => setTabMode(key)}
        items={[
          {
            key: "ALL",
            label: `ทั้งหมด (${dataCount?.ALL || 0})`,
          },
          {
            key: "QUOTA_TOTAL",
            label: `โควตารวม (${dataCount?.QUOTA_TOTAL || 0})`,
          },
          {
            key: "QUOTA_INDIVIDUAL",
            label: `โควตารายร้าน (${dataCount?.QUOTA_INDIVIDUAL || 0})`,
          },
          {
            key: "QUOTA_NONE",
            label: `ไม่มีโควตา (${dataCount?.QUOTA_NONE || 0})`,
          },
        ]}
      />
      <>
        {data?.filter((item: any) => (tabMode !== "ALL" ? item.quotaType === tabMode : true))
          .length > 0 ? (
          <div style={{ maxHeight: 400, overflowY: "auto", padding: 16 }}>
            {data
              ?.filter((item: any) => (tabMode !== "ALL" ? item.quotaType === tabMode : true))
              .map((item, index) => (
                <React.Fragment key={index}>
                  <Row justify='space-between' key={index}>
                    <Col>
                      <Text fontWeight={700}>{item.productName}</Text> &nbsp;
                      <Text color='Text3'>({item.packSize})</Text>&nbsp;
                      <Tag color='#EFF2F9'>
                        <Text level={6} fontWeight={700}>
                          {mapQuotaType[item.quotaType]}
                        </Text>
                      </Tag>
                    </Col>
                    <Col>
                      <Text>
                        <b>{numberFormatter(item.soldUnits, digitDecimal)}</b>{" "}
                        {item.quotaAmount &&
                          `/ ${numberFormatter(item.quotaAmount, digitDecimal)} `}
                        <Text color='Text3'>{item.saleUomTh} </Text>
                        {item.quotaType !== "QUOTA_NONE" &&
                          (item.soldPercent > 100 ? `(100  %)` : `(${item.soldPercent} %)`)}
                      </Text>
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24}>
                      <Progress
                        percent={item.soldPercent > 100 ? 100 : item.soldPercent}
                        showInfo={false}
                      />
                    </Col>
                  </Row>
                  <Divider size='small' />
                </React.Fragment>
              ))}
          </div>
        ) : (
          <FlexCol justify='center' align='center' style={{ height: "70%" }}>
            <img
              src={icon.emptyHistoryPoint}
              style={{
                width: 80,
                height: 90,
                alignItems: "center",
              }}
              alt='empty_table'
            />
            <Text fontSize={16} fontWeight={400} color='Text3' style={{ paddingTop: 16 }}>
              ไม่มีข้อมูล
            </Text>
          </FlexCol>
        )}
      </>
    </Card>
  );
};

const SectionOrder = ({
  data,
  company,
  filter,
  currentRound,
  dataRounds,
  searchOrder,
  setSearchOrder,
}: {
  data: any;
  company: any;
  filter: any;
  currentRound: any;
  dataRounds: any;
  searchOrder: any;
  setSearchOrder: any;
}) => {
  const column = [
    {
      title: "Order No.",
      dataIndex: "orderNo",
      key: "orderNo",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{value}</Text>
              <Text color='Text3'>{dayjs(row?.createdAt).format("DD/MM/YYYY HH:mm")}</Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "ชื่อร้าน",
      dataIndex: "customerName",
      key: "customerName",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{value}</Text>
              <Text color='Text3'>
                {row?.customerNo} • เขต {row?.zone} • กลุ่ม {row?.customerGroupName || "-"}
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "รอบ",
      dataIndex: "roundNo",
      key: "roundNo",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>รอบที่ {value}</Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "ยอดสั่งซื้อ (฿)",
      dataIndex: "totalPrice",
      key: "totalPrice",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{numberFormatter(value, 2)}</Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      key: "status",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text
                level={5}
                fontWeight={700}
                style={{
                  color: ORDER_STATUS[value]?.text_color,
                  backgroundColor: ORDER_STATUS[value]?.color,
                  padding: "3px 8px 2px 8px",
                  borderRadius: 4,
                  textAlign: "center",
                  fontSize: 14,
                }}
              >
                {getOrderStatus(value, company, row?.isOverCredit) || "-"}
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "",
      dataIndex: "action",
      key: "action",
      width: "8%",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <ActionBtn
                onClick={() => {
                  window.open(`/order/${row.orderId}`, "_blank");
                }}
                icon={<UnorderedListOutlined />}
              />
            </FlexCol>
          ),
        };
      },
    },
  ];

  return (
    <Card>
      <div style={{ marginBottom: "10px" }}>
        <Text fontWeight={700} level={4}>
          รายการคำสั่งซื้อ
        </Text>
      </div>
      <Row justify={"start"} gutter={8} style={{ paddingBottom: "16px" }}>
        <Col span={8}>
          <Input
            value={searchOrder.search}
            placeholder='ค้นหารหัสคำสั่งซื้อ / รหัสร้านค้า / ชื่อร้านค้า'
            style={{ width: "100%", height: "40px" }}
            suffix={<SearchOutlined />}
            allowClear
            onChange={(e) => {
              setSearchOrder((prev: any) => ({ ...prev, search: e.target.value, page: 1 }));
            }}
            // onPressEnter={(e: any) => {
            //   setSearchOrder((prev: any) => ({ ...prev, search: e.target.value, page: 1 }));
            // }}
          />
        </Col>
        <Col span={3}>
          <Select
            value={searchOrder.zone}
            allowClear
            options={
              filter?.zones?.map((zone) => ({
                label: zone,
                value: zone,
                key: zone,
              })) || []
            }
            placeholder='เขตทั้งหมด'
            style={{ width: "100%", height: "40px" }}
            onChange={(e) => {
              setSearchOrder((prev: any) => ({ ...prev, zone: e, page: 1 }));
            }}
          />
        </Col>
        <Col span={4}>
          <Select
            value={searchOrder.customerGroupName}
            allowClear
            options={
              filter?.customerGroupNames?.map((group: any) => ({
                label: group,
                value: group,
                key: group,
              })) || []
            }
            placeholder='กลุ่มลูกค้าทั้งหมด'
            style={{ width: "100%", height: "40px" }}
            onChange={(e) => {
              setSearchOrder((prev: any) => ({ ...prev, customerGroupName: e, page: 1 }));
            }}
          />
        </Col>
        {currentRound === "ALL" && (
          <Col span={3}>
            <Select
              allowClear
              value={
                searchOrder.flashSaleRoundId === "ALL" ? undefined : searchOrder.flashSaleRoundId
              }
              options={
                dataRounds?.map((round) => ({
                  label: `รอบที่ ${round?.roundNo}`,
                  value: round?.flashSaleRoundId,
                  key: round?.flashSaleRoundId,
                })) || []
              }
              placeholder='ทุกรอบ'
              style={{ width: "100%", height: "40px" }}
              onChange={(e) => {
                setSearchOrder((prev: any) => ({ ...prev, flashSaleRoundId: e, page: 1 }));
              }}
            />
          </Col>
        )}

        <Col span={3}>
          <Button
            typeButton='primary-light'
            title='ล้างการค้นหา'
            onClick={() => {
              setSearchOrder({
                flashSaleRoundId: currentRound === "ALL" ? undefined : searchOrder.flashSaleRoundId,
                search: undefined,
                zone: undefined,
                customerGroupName: undefined,
                page: 1,
              });
            }}
          />
        </Col>
        <Col flex='1' style={{ paddingTop: "8px", textAlign: "right" }}>
          <Text color='Text3'>{data?.count || 0} รายการ</Text>
        </Col>
      </Row>
      <ConfigProvider
        theme={{
          components: {
            Table: {
              headerBg: "#F3F6F9",
              headerColor: "black",
              fontFamily: "IBM Plex Sans Thai",
              fontSize: 15,
            },
          },
        }}
      >
        <Table
          columns={column}
          dataSource={data?.data || []}
          pagination={{
            showSizeChanger: false,
            current: searchOrder.page,
            total: data?.count || 0,
            pageSize: 10,
            position: ["bottomCenter"],
            onChange: (page) => {
              setSearchOrder((prev: any) => ({ ...prev, page }));
            },
          }}
        />
      </ConfigProvider>
    </Card>
  );
};
